/**
 * Stage B/F — the location truth vocabulary, and the pure rules over it.
 *
 * Two authorities, never one field:
 *   CURRENT PHYSICAL LOCATION   where the vehicle is believed to be now
 *                               (`vehicle_location_current`, one event behind it)
 *   PLANNED LOCATION            where OptiBay intends it to go
 *                               (`ticket_location_plan`)
 *
 * Planning is not observation: nothing here derives one from the other.
 * Presentation-agnostic — no React, no DOM, no Supabase.
 */

export const LOCATION_KINDS = [
  'BAY',
  'PARKING_SPOT',
  'ON_SITE_GENERAL',
  'ON_SITE_UNKNOWN_EXACT',
  'OFF_SITE',
  'NOT_PRESENT',
] as const;
export type LocationKind = (typeof LOCATION_KINDS)[number];

/** Kinds a person can move a vehicle TO (NOT_PRESENT is recorded by pickup). */
export const MOVABLE_LOCATION_KINDS = [
  'BAY',
  'PARKING_SPOT',
  'ON_SITE_GENERAL',
  'ON_SITE_UNKNOWN_EXACT',
  'OFF_SITE',
] as const;
export type MovableLocationKind = (typeof MOVABLE_LOCATION_KINDS)[number];

export const OFF_SITE_REASONS = ['TEST_DRIVE', 'SUBLET', 'OTHER'] as const;
export type OffSiteReason = (typeof OFF_SITE_REASONS)[number];

export const LOCATION_SOURCES = [
  'HUMAN_CORRECTION',
  'TECH_PULL_IN',
  'TECH_PARK',
  'FOH_MOVE',
  'SYSTEM_WORKFLOW',
  'CAMERA_OBSERVATION',
  'LPR_OBSERVATION',
  'IMPORT',
  'UNKNOWN',
] as const;
export type LocationSource = (typeof LOCATION_SOURCES)[number];

/** AUTHORITATIVE = a deterministic human/workflow act; OBSERVED = a sensor
 *  with a probability; INFERRED = derived without direct evidence. */
export type LocationConfidenceClass = 'AUTHORITATIVE' | 'OBSERVED' | 'INFERRED';

export type LocationPlanSource =
  | 'SCHEDULING'
  | 'OPTIQUEUE_BOOKING'
  | 'EOD_ROLLOVER'
  | 'MANUAL_SPLIT'
  | 'SPECIALTY_TASK'
  | 'OPTIPLACE'
  | 'FOH'
  | 'LEGACY_MIGRATION';

/** "Where is this vehicle believed to be right now?" — one typed answer. */
export interface CurrentVehicleLocation {
  ticketId: string;
  kind: LocationKind;
  bayId: string | null;
  parkingSpotId: string | null;
  offSiteReason: OffSiteReason | null;
  source: LocationSource;
  confidenceClass: LocationConfidenceClass;
  confidence: number | null;
  observedAt: string;
  eventId: string;
}

/** "Where is this vehicle planned to go?" — a separate answer. */
export interface VehicleLocationPlan {
  ticketId: string;
  kind: 'BAY' | 'PARKING_SPOT';
  bayId: string | null;
  parkingSpotId: string | null;
  plannedFor: string | null;
  source: LocationPlanSource;
  reason: string | null;
}

/** A physical location a person asks to move a vehicle to. */
export type VehicleLocationTarget =
  | { kind: 'BAY'; bayId: string }
  | { kind: 'PARKING_SPOT'; parkingSpotId: string }
  | { kind: 'ON_SITE_GENERAL' }
  | { kind: 'ON_SITE_UNKNOWN_EXACT' }
  | { kind: 'OFF_SITE'; reason: OffSiteReason };

export interface CurrentLocationRow {
  ticket_id: string;
  location_kind: string;
  bay_id: string | null;
  parking_spot_id: string | null;
  off_site_reason: string | null;
  source: string;
  confidence_class: string;
  confidence: number | null;
  observed_at: string;
  event_id: string;
}

export interface LocationPlanRow {
  ticket_id: string;
  location_kind: string;
  bay_id: string | null;
  parking_spot_id: string | null;
  planned_for: string | null;
  source: string;
  reason: string | null;
}

export function currentLocationFromRow(row: CurrentLocationRow): CurrentVehicleLocation {
  return {
    ticketId: row.ticket_id,
    kind: row.location_kind as LocationKind,
    bayId: row.bay_id,
    parkingSpotId: row.parking_spot_id,
    offSiteReason: (row.off_site_reason as OffSiteReason | null) ?? null,
    source: row.source as LocationSource,
    confidenceClass: row.confidence_class as LocationConfidenceClass,
    confidence: row.confidence,
    observedAt: row.observed_at,
    eventId: row.event_id,
  };
}

export function locationPlanFromRow(row: LocationPlanRow): VehicleLocationPlan {
  return {
    ticketId: row.ticket_id,
    kind: row.location_kind as 'BAY' | 'PARKING_SPOT',
    bayId: row.bay_id,
    parkingSpotId: row.parking_spot_id,
    plannedFor: row.planned_for,
    source: row.source as LocationPlanSource,
    reason: row.reason,
  };
}

/** On the premises (whether or not we know exactly where). */
export function isOnSite(loc: CurrentVehicleLocation | null | undefined): boolean {
  return !!loc && (loc.kind === 'BAY' || loc.kind === 'PARKING_SPOT'
    || loc.kind === 'ON_SITE_GENERAL' || loc.kind === 'ON_SITE_UNKNOWN_EXACT');
}

/** The bay this vehicle PHYSICALLY occupies — never a planned bay. */
export function occupiedBayId(loc: CurrentVehicleLocation | null | undefined): string | null {
  return loc?.kind === 'BAY' ? loc.bayId : null;
}

/** The parking spot this vehicle PHYSICALLY occupies. */
export function occupiedParkingSpotId(loc: CurrentVehicleLocation | null | undefined): string | null {
  return loc?.kind === 'PARKING_SPOT' ? loc.parkingSpotId : null;
}

/**
 * The legacy compatibility fields implied by current truth (the same
 * projection the server writes). Unknown-exact and general both read as
 * "in the lot" — the legacy columns cannot say more.
 */
export function legacyFieldsOf(loc: CurrentVehicleLocation | null | undefined): {
  bay_id: string | null; parking_spot_id: string | null; parking_lot: boolean;
} {
  if (!loc) return { bay_id: null, parking_spot_id: null, parking_lot: false };
  return {
    bay_id: loc.kind === 'BAY' ? loc.bayId : null,
    parking_spot_id: loc.kind === 'PARKING_SPOT' ? loc.parkingSpotId : null,
    parking_lot: loc.kind === 'PARKING_SPOT' || loc.kind === 'ON_SITE_GENERAL' || loc.kind === 'ON_SITE_UNKNOWN_EXACT',
  };
}

type WithLegacyLocation = {
  id: string;
  bay_id?: string | null;
  parking_spot_id?: string | null;
  parking_lot?: boolean | null;
  rollover_of_ticket_id?: string | null;
  parent_ticket_id?: string | null;
};

/**
 * The ticket that HOLDS the vehicle's physical truth. A rollover continuation
 * or a specialty sub-ticket is another work ticket for the same car
 * (`parent_ticket_id` points at the chain root; `rollover_of_ticket_id`
 * otherwise) — the server's `vehicle_location_holder`, mirrored.
 */
export function locationHolderId(ticket: {
  id: string; parent_ticket_id?: string | null; rollover_of_ticket_id?: string | null;
}): string {
  return ticket.parent_ticket_id ?? ticket.rollover_of_ticket_id ?? ticket.id;
}

/** True when this ticket is another work ticket for a car held by a different ticket. */
export function isDependentTicket(ticket: {
  parent_ticket_id?: string | null; rollover_of_ticket_id?: string | null;
}): boolean {
  return !!(ticket.parent_ticket_id || ticket.rollover_of_ticket_id);
}

/**
 * Stage B/F.1 — the bay a ticket's OWN legacy columns may be read as
 * physically occupying, for surfaces that have only the ticket row. A
 * dependent ticket's `bay_id` is a PLAN encoding (continuation / specialty
 * bay) and has zero physical authority: null. For a vehicle holder the legacy
 * bay is the server's projection of current physical truth.
 */
export function legacyPhysicalBayId(ticket: {
  bay_id?: string | null; parking_lot?: boolean | null;
  parent_ticket_id?: string | null; rollover_of_ticket_id?: string | null;
}): string | null {
  if (isDependentTicket(ticket)) return null;
  return ticket.bay_id && ticket.parking_lot !== true ? ticket.bay_id : null;
}

/**
 * Place a ticket by CURRENT PHYSICAL TRUTH. Physical surfaces (Birdseye,
 * ShopFloor) run every ticket through this before drawing it, so a bay that
 * is only planned — a booking, a specialty task, a continuation's legacy
 * `bay_id` — can never put a car in a bay. A dependent ticket is drawn by its
 * own recorded evidence if any, else where its holder (the same car) is. No
 * truth anywhere = drawn nowhere physical (not arrived / no evidence).
 */
export function placeByPhysicalTruth<T extends WithLegacyLocation>(
  ticket: T,
  currentByTicket: ReadonlyMap<string, CurrentVehicleLocation>,
  holderId: string = locationHolderId(ticket),
): T {
  const truth = currentByTicket.get(ticket.id)
    ?? (holderId !== ticket.id ? currentByTicket.get(holderId) : undefined);
  return { ...ticket, ...legacyFieldsOf(truth) };
}

/**
 * When current truth could not be read: the legacy columns are the server's
 * projection of it for the holder, but a dependent ticket's legacy `bay_id`
 * is a PLAN — never a place to draw a car.
 */
export function placeByLegacyProjection<T extends WithLegacyLocation>(
  ticket: T,
  holderId: string = locationHolderId(ticket),
): T {
  if (holderId === ticket.id) return ticket;
  return { ...ticket, bay_id: null, parking_spot_id: null, parking_lot: false };
}

/** Build the lookup a surface uses from rows it read or received. */
export function indexCurrentLocations(
  rows: readonly CurrentLocationRow[],
): Map<string, CurrentVehicleLocation> {
  const out = new Map<string, CurrentVehicleLocation>();
  for (const r of rows) out.set(r.ticket_id, currentLocationFromRow(r));
  return out;
}

/**
 * Oct 20 addendum L5 — who PHYSICALLY moved the vehicle, as the recording employee attests it:
 *   { kind: 'self' }                     "I moved it" — the recorder is the mover;
 *   { kind: 'onBehalf', moverUserId }    recorded on behalf of another employee, who moved it.
 * Omitted = not attested: the server records "mover not recorded" and never assumes the recorder moved it.
 */
export type MoverAttestation = { kind: 'self' } | { kind: 'onBehalf'; moverUserId: string };

/** The request body a physical move sends — one shape for every surface. */
export function moveRequestArgs(ticketId: string, target: VehicleLocationTarget, opts: {
  reason?: string | null; requestId?: string | null; correction?: boolean; mover?: MoverAttestation | null;
} = {}) {
  return {
    p_ticket_id: ticketId,
    p_target_bay_id: target.kind === 'BAY' ? target.bayId : undefined,
    p_target_parking_spot_id: target.kind === 'PARKING_SPOT' ? target.parkingSpotId : undefined,
    p_location_kind: target.kind,
    p_off_site_reason: target.kind === 'OFF_SITE' ? target.reason : undefined,
    p_reason: opts.reason ?? undefined,
    p_request_id: opts.requestId ?? undefined,
    p_is_correction: opts.correction ?? false,
    // L5: only sent when attested (a correction never names a mover).
    ...(opts.mover && !opts.correction
      ? opts.mover.kind === 'self'
        ? { p_mover_attestation: 'SELF' as const }
        : { p_mover_attestation: 'ON_BEHALF' as const, p_moved_by: opts.mover.moverUserId }
      : {}),
  };
}
